import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  FileText, Receipt, Upload, Building2, Calendar, HelpCircle, Landmark, CircleDollarSign, AlertOctagon, FolderKanban,
  ChevronLeft, ChevronRight, Check, AlertTriangle
} from 'lucide-react';
import { useTenant } from '../../context/TenantContext';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

// Form components for each request type
import EmitirFacturaForm from '../../components/requests/EmitirFacturaForm';
import RegistrarGastoForm from '../../components/requests/RegistrarGastoForm';
import SubidaMasivaForm from '../../components/requests/SubidaMasivaForm';
import ConciliacionForm from '../../components/requests/ConciliacionForm';
import CierreMensualForm from '../../components/requests/CierreMensualForm';
import SolicitudGeneralForm from '../../components/requests/SolicitudGeneralForm';
import ControlCobrosPagosForm from '../../components/requests/ControlCobrosPagosForm';
import SeguimientoImpagosForm from '../../components/requests/SeguimientoImpagosForm';
import InformesMensualesForm from '../../components/requests/InformesMensualesForm';
import GestoriaMensualForm from '../../components/requests/GestoriaMensualForm';

const REQUIRED_ATTACHMENT_TYPES = new Set([
  'emitir_factura',
  'registrar_gasto',
  'subida_masiva_gastos',
  'conciliacion_bancaria',
  'control_cobros_pagos',
  'cierre_mensual',
  'seguimiento_impagos',
  'informes_mensuales',
  'gestoria_mensual',
]);

const REQUEST_TYPE_ALIASES = {
  conciliacion_extractos: 'conciliacion_bancaria',
  modelos_impuestos: 'informes_mensuales',
  facturacion_recurrente: 'emitir_factura',
};

const REQUEST_TYPE_CONFIG = {
  emitir_factura: {
    icon: FileText,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50 text-blue-700',
    component: EmitirFacturaForm
  },
  registrar_gasto: {
    icon: Receipt,
    color: 'bg-green-500',
    lightColor: 'bg-green-50 text-green-700',
    component: RegistrarGastoForm
  },
  subida_masiva_gastos: {
    icon: Upload,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50 text-amber-700',
    component: SubidaMasivaForm
  },
  conciliacion_bancaria: {
    icon: Building2,
    color: 'bg-cyan-500',
    lightColor: 'bg-cyan-50 text-cyan-700',
    component: ConciliacionForm
  },
  control_cobros_pagos: {
    icon: CircleDollarSign,
    color: 'bg-teal-600',
    lightColor: 'bg-teal-50 text-teal-700',
    component: ControlCobrosPagosForm
  },
  cierre_mensual: {
    icon: Calendar,
    color: 'bg-rose-500',
    lightColor: 'bg-rose-50 text-rose-700',
    component: CierreMensualForm
  },
  seguimiento_impagos: {
    icon: AlertOctagon,
    color: 'bg-red-600',
    lightColor: 'bg-red-50 text-red-700',
    component: SeguimientoImpagosForm
  },
  informes_mensuales: {
    icon: Landmark,
    color: 'bg-indigo-500',
    lightColor: 'bg-indigo-50 text-indigo-700',
    component: InformesMensualesForm
  },
  gestoria_mensual: {
    icon: FolderKanban,
    color: 'bg-fuchsia-600',
    lightColor: 'bg-fuchsia-50 text-fuchsia-700',
    component: GestoriaMensualForm
  },
  solicitud_general: {
    icon: HelpCircle,
    color: 'bg-slate-500',
    lightColor: 'bg-slate-50 text-slate-700',
    component: SolicitudGeneralForm
  }
};

export default function RequestWizard() {
  const navigate = useNavigate();
  const { activeTenant, onboardingStatus } = useTenant();
  const { isOperator } = useAuth();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Select Type, 2: Fill Form, 3: Confirm, 4: Success
  const [requestTypes, setRequestTypes] = useState({});
  const [usageStats, setUsageStats] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createdRequest, setCreatedRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const isReadyToOperate = onboardingStatus?.is_ready_to_operate === true;
  const canOperate = isOperator() || isReadyToOperate;

  useEffect(() => {
    if (activeTenant) {
      loadData();
    }
  }, [activeTenant, searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesRes, usageRes] = await Promise.all([
        api.get('/request-types'),
        api.get(`/usage/${activeTenant}`)
      ]);
      setRequestTypes(typesRes.data);
      setUsageStats(usageRes.data);
      const docsRes = await api.get(`/documents?tenant_id=${activeTenant}`);
      setDocuments(docsRes.data || []);
      const initialTypeRaw = searchParams.get('type');
      const initialType = REQUEST_TYPE_ALIASES[initialTypeRaw] || initialTypeRaw;
      const isAllowedByPack = Array.isArray(usageRes.data?.allowed_types)
        ? usageRes.data.allowed_types.includes(initialType)
        : true;
      if (initialType && typesRes.data?.[initialType] && isAllowedByPack) {
        setSelectedType(initialType);
        setFormData({});
        setAttachments([]);
        setStep(2);
      } else if (initialType && typesRes.data?.[initialType] && !isAllowedByPack) {
        toast.error('El modulo solicitado no esta incluido en tu plan');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (typeKey) => {
    if (!canOperate) {
      toast.error('Cuenta en revision');
      return;
    }
    const isAllowedByPack = Array.isArray(usageStats?.allowed_types)
      ? usageStats.allowed_types.includes(typeKey)
      : true;
    if (!isAllowedByPack) {
      toast.error('Este modulo no esta incluido en tu plan actual');
      return;
    }
    // Check if blocked
    if (usageStats?.blocked_types?.includes(typeKey)) {
      toast.error('Has alcanzado el límite mensual para este tipo de solicitud');
      return;
    }
    setSelectedType(typeKey);
    setFormData({});
    setAttachments([]);
    setStep(2);
  };

  const handleFormSubmit = (data) => {
    setFormData(data);
    setStep(3);
  };

  const handleConfirm = async () => {
    if (requiresAttachments && attachments.length === 0) {
      toast.error('Debes adjuntar al menos un documento para esta solicitud');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.post('/requests/wizard', {
        tenant_id: activeTenant,
        request_type: selectedType,
        form_data: formData,
        attachments
      });
      setCreatedRequest(response.data);
      setStep(4);
      toast.success('¡Solicitud creada exitosamente!');
    } catch (error) {
      console.error('Error creating request:', error);
      const errorMsg = error.response?.data?.detail || 'Error al crear la solicitud';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setSelectedType(null);
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const handleNewRequest = () => {
    setSelectedType(null);
    setFormData({});
    setAttachments([]);
    setCreatedRequest(null);
    setStep(1);
    loadData(); // Refresh usage stats
  };

  const handleAttachmentToggle = (docId, checked) => {
    if (checked) {
      setAttachments((prev) => (prev.includes(docId) ? prev : [...prev, docId]));
      return;
    }
    setAttachments((prev) => prev.filter((id) => id !== docId));
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeTenant) return;
    try {
      setUploadingFile(true);
      const payload = new FormData();
      payload.append('file', file);
      const response = await api.post('/documents/upload', payload, {
        params: {
          tenant_id: activeTenant,
          document_type: 'request_support'
        },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newDoc = response.data;
      setDocuments((prev) => [newDoc, ...prev]);
      setAttachments((prev) => (prev.includes(newDoc.id) ? prev : [...prev, newDoc.id]));
      toast.success('Documento adjuntado');
    } catch (error) {
      toast.error('No se pudo subir el adjunto');
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  const requiresAttachments = selectedType ? REQUIRED_ATTACHMENT_TYPES.has(selectedType) : false;

  const getUsageForType = (typeKey) => {
    if (!usageStats) return { usage: 0, limit: null, percentage: 0 };
    return {
      usage: usageStats.request_counts?.[typeKey] || 0,
      limit: usageStats.limits?.[typeKey],
      percentage: usageStats.percentages?.[typeKey] || 0
    };
  };

  const visibleRequestTypes = useMemo(() => {
    const entries = Object.entries(requestTypes || {});
    if (!Array.isArray(usageStats?.allowed_types)) {
      return entries;
    }
    const allowed = new Set(usageStats.allowed_types);
    return entries.filter(([key]) => allowed.has(key));
  }, [requestTypes, usageStats?.allowed_types]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Step 1: Type Selection
  if (step === 1) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Nueva Solicitud</h1>
          <p className="text-slate-600">Selecciona el tipo de solicitud que deseas crear</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleRequestTypes.map(([key, type]) => {
            const config = REQUEST_TYPE_CONFIG[key] || {};
            const Icon = config.icon || FileText;
            const usage = getUsageForType(key);
            const isBlocked = usageStats?.blocked_types?.includes(key);
            const isWarning = usageStats?.warning_types?.includes(key);
            
            return (
              <Card 
                key={key}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  isBlocked ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary'
                }`}
                onClick={() => !isBlocked && handleSelectType(key)}
                data-testid={`request-type-${key}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${config.color || 'bg-slate-500'} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 mb-1">{type.name}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{type.description}</p>
                      
                      {usage.limit && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={isBlocked ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-500'}>
                              {usage.usage} / {usage.limit} este mes
                            </span>
                            {isBlocked && <Badge variant="destructive" className="text-xs">Límite</Badge>}
                            {isWarning && !isBlocked && <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">80%+</Badge>}
                          </div>
                          <Progress 
                            value={Math.min(usage.percentage, 100)} 
                            className={`h-1.5 ${isBlocked ? '[&>div]:bg-red-500' : isWarning ? '[&>div]:bg-amber-500' : ''}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {visibleRequestTypes.length === 0 && (
          <Alert className="mt-6">
            <AlertDescription>
              No hay modulos habilitados para tu plan actual.
            </AlertDescription>
          </Alert>
        )}

        {usageStats?.blocked_types?.length > 0 && (
          <Alert className="mt-6 bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              Has alcanzado el límite mensual en algunos tipos de solicitud. 
              <a href="/client/billing" className="underline ml-1">Actualiza tu plan</a> para aumentar los límites.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Step 2: Form
  if (step === 2 && selectedType) {
    const typeInfo = requestTypes[selectedType] || {};
    const config = REQUEST_TYPE_CONFIG[selectedType] || {};
    const FormComponent = config.component;
    const Icon = config.icon || FileText;

    return (
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={handleBack} className="mb-4" data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Volver
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${config.color || 'bg-slate-500'} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle>{typeInfo.name}</CardTitle>
                <CardDescription>{typeInfo.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {FormComponent && (
              <FormComponent 
                initialData={formData}
                onSubmit={handleFormSubmit}
                tenantId={activeTenant}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Confirmation
  if (step === 3) {
    const typeInfo = requestTypes[selectedType] || {};
    const config = REQUEST_TYPE_CONFIG[selectedType] || {};
    const Icon = config.icon || FileText;

    return (
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={handleBack} className="mb-4" data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Volver
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Confirmar Solicitud</CardTitle>
            <CardDescription>Revisa los datos antes de enviar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <div className={`w-10 h-10 rounded-lg ${config.color || 'bg-slate-500'} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium">{typeInfo.name}</div>
                <div className="text-sm text-slate-600">{typeInfo.description}</div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">Datos de la solicitud:</h4>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                {Object.entries(formData).map(([key, value]) => {
                  if (value === null || value === undefined || value === '' || key.startsWith('_')) return null;
                  if (Array.isArray(value)) {
                    return (
                      <div key={key} className="text-sm">
                        <span className="text-slate-600">{formatFieldName(key)}:</span>
                        <span className="ml-2 font-medium">{value.length} elementos</span>
                      </div>
                    );
                  }
                  if (typeof value === 'object') return null;
                  return (
                    <div key={key} className="text-sm flex justify-between">
                      <span className="text-slate-600">{formatFieldName(key)}:</span>
                      <span className="font-medium">{formatFieldValue(value)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-slate-900">
                  Adjuntos {requiresAttachments ? <span className="text-red-600">*</span> : null}
                </h4>
                <label className="inline-flex">
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleUploadAttachment}
                    disabled={uploadingFile}
                  />
                  <span className="text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                    {uploadingFile ? 'Subiendo...' : 'Subir archivo'}
                  </span>
                </label>
              </div>
              {requiresAttachments && attachments.length === 0 && (
                <p className="text-xs text-red-600">
                  Este tipo de solicitud requiere al menos un adjunto.
                </p>
              )}
              {documents.length === 0 ? (
                <div className="text-sm text-slate-500 border rounded-lg p-3">
                  No hay documentos disponibles para adjuntar.
                </div>
              ) : (
                <div className="max-h-52 overflow-auto space-y-2 border rounded-lg p-3">
                  {documents.map((doc) => (
                    <label key={doc.id} className="flex items-center justify-between gap-3 text-sm cursor-pointer">
                      <span className="truncate">{doc.filename}</span>
                      <input
                        type="checkbox"
                        checked={attachments.includes(doc.id)}
                        onChange={(e) => handleAttachmentToggle(doc.id, e.target.checked)}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800 text-sm">
                Al confirmar, se creará una tarea automática para nuestro equipo con el SLA correspondiente a tu plan.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} className="flex-1" disabled={submitting}>
                Modificar
              </Button>
              <Button onClick={handleConfirm} className="flex-1" disabled={submitting} data-testid="btn-confirm">
                {submitting ? 'Enviando...' : 'Confirmar y Enviar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Success
  if (step === 4 && createdRequest) {
    const typeInfo = requestTypes[selectedType] || {};
    const config = REQUEST_TYPE_CONFIG[selectedType] || {};

    return (
      <div className="max-w-2xl mx-auto text-center">
        <Card>
          <CardContent className="py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Solicitud Enviada!</h2>
            <p className="text-slate-600 mb-6">
              Tu solicitud de "{typeInfo.name}" ha sido creada correctamente.
            </p>

            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">ID Solicitud:</span>
                  <div className="font-mono text-xs mt-1">{createdRequest.id.slice(0, 8)}...</div>
                </div>
                <div>
                  <span className="text-slate-600">Estado:</span>
                  <div className="mt-1">
                    <Badge variant="secondary">{createdRequest.status}</Badge>
                  </div>
                </div>
                {createdRequest.tasks?.[0] && (
                  <>
                    <div>
                      <span className="text-slate-600">Fecha límite:</span>
                      <div className="mt-1 font-medium">
                        {new Date(createdRequest.tasks[0].due_date).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-600">Prioridad:</span>
                      <div className="mt-1">
                        <Badge variant={createdRequest.tasks[0].priority === 'high' ? 'destructive' : 'outline'}>
                          {createdRequest.tasks[0].priority === 'high' ? 'Alta' : 'Media'}
                        </Badge>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleNewRequest} className="flex-1">
                Nueva Solicitud
              </Button>
              <Button onClick={() => navigate('/client/requests')} className="flex-1">
                Ver Mis Solicitudes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

// Helper functions
function formatFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

function formatFieldValue(value) {
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return value.toLocaleString('es-ES');
  return String(value);
}
